// A busca dentro do agente. O que ela resolve: são seis seções e algumas dezenas de campos,
// e quem procura "follow-up" não sabe (nem devia precisar saber) que ele mora numa seção
// própria, ou que "quanto tempo o agente espera antes de responder" é o debounce, dentro de
// Settings.
//
// O índice é DECLARADO e não varrido do DOM. Varrer parece mais esperto e é pior aqui: só a
// seção ativa está montada, então uma varredura só acharia o que já está na tela — que é
// exatamente o que ninguém precisa procurar.
//
// `anchor` casa com um atributo `data-busca` no campo. É ele que permite grifar o lugar
// certo em vez de só abrir a seção e deixar a pessoa procurar de novo.

// Cada entrada: onde fica, o que grifar, e por quais palavras se procura. Os `termos` são o
// vocabulário de quem procura, não o do código: quem quer o follow-up digita "followup",
// "acompanhamento" ou "cobrança", e nenhum dos três é o nome da chave.
export const INDICE = [
  {
    id: 'followup',
    section: 'followup',
    anchor: 'followup-enabled',
    titulo: 'POWER_AGENTS.FOLLOWUP.ENABLED',
    onde: 'POWER_AGENTS.SECTIONS.FOLLOWUP',
    termos: [
      'follow-up',
      'followup',
      'follow up',
      'acompanhamento',
      'retomar',
      'cobrança',
      'conversa parada',
      'quiet',
      'reengajar',
    ],
  },
  {
    id: 'followup-stages',
    section: 'followup',
    anchor: 'followup-stages',
    titulo: 'POWER_AGENTS.FOLLOWUP.STAGES',
    onde: 'POWER_AGENTS.SECTIONS.FOLLOWUP',
    termos: [
      'estágios',
      'estagios',
      'stages',
      'quantidade de disparos',
      'quantas vezes',
      'tentativas',
      'cadência',
      'cadencia',
    ],
  },
  {
    id: 'followup-hours',
    section: 'followup',
    anchor: 'followup-hours',
    titulo: 'POWER_AGENTS.FOLLOWUP.HOURS',
    onde: 'POWER_AGENTS.SECTIONS.FOLLOWUP',
    termos: [
      'horário permitido',
      'horario permitido',
      'allowed hours',
      'janela',
      'madrugada',
      'horário',
      'horario',
      'hours',
    ],
  },
  {
    id: 'followup-conditions',
    section: 'followup',
    anchor: 'followup-conditions',
    titulo: 'POWER_AGENTS.FOLLOWUP.CONDITIONS',
    onde: 'POWER_AGENTS.SECTIONS.FOLLOWUP',
    termos: [
      'condição',
      'condicao',
      'condições',
      'condicoes',
      'regra',
      'parar',
      'não insistir',
      'nao insistir',
      'encerrar',
      'já comprou',
      'ja comprou',
    ],
  },
  {
    id: 'audio',
    section: 'audio',
    anchor: 'audio-enabled',
    titulo: 'POWER_AGENTS.AUDIO.ENABLED',
    onde: 'POWER_AGENTS.SECTIONS.AUDIO',
    termos: [
      'audio',
      'áudio',
      'nota de voz',
      'transcrever',
      'transcrição',
      'transcricao',
      'ouvir',
      'voz',
      'whisper',
    ],
  },
  {
    id: 'audio-anexos',
    section: 'audio',
    anchor: 'audio-anexos',
    titulo: 'POWER_AGENTS.AUDIO.ATTACHMENTS',
    onde: 'POWER_AGENTS.SECTIONS.AUDIO',
    termos: [
      'anexo',
      'anexos',
      'imagem',
      'imagens',
      'foto',
      'fotos',
      'ver imagem',
      'olhar foto',
      'pdf',
      'documento',
      'arquivo',
      'visão',
      'visao',
    ],
  },
  {
    id: 'audio-rajada-texto',
    section: 'audio',
    anchor: 'audio-rajada',
    titulo: 'POWER_AGENTS.AUDIO.NEEDS_TEXT',
    onde: 'POWER_AGENTS.SECTIONS.AUDIO',
    termos: [
      'rajada',
      'muitas fotos',
      'varias fotos',
      'responde cada foto',
      'responder uma vez',
      'so com texto',
      'esperar texto',
      'agrupar anexos',
    ],
  },
  {
    id: 'audio-rajada-carencia',
    section: 'audio',
    anchor: 'audio-rajada',
    titulo: 'POWER_AGENTS.AUDIO.DEBOUNCE',
    onde: 'POWER_AGENTS.SECTIONS.AUDIO',
    termos: [
      'carencia',
      'carência',
      'esperar foto',
      'atraso anexo',
      'juntar fotos',
      'debounce de anexo',
    ],
  },
  {
    id: 'audio-rajada-teto',
    section: 'audio',
    anchor: 'audio-rajada',
    titulo: 'POWER_AGENTS.AUDIO.BURST_MAX',
    onde: 'POWER_AGENTS.SECTIONS.AUDIO',
    termos: [
      'quantos anexos',
      'limite de fotos',
      'teto de anexos',
      'maximo de anexos',
      'máximo de anexos',
      '80 fotos',
    ],
  },
  {
    id: 'audio-key',
    section: 'audio',
    anchor: 'audio-key',
    titulo: 'POWER_AGENTS.AUDIO.KEY',
    onde: 'POWER_AGENTS.SECTIONS.AUDIO',
    termos: [
      'chave de transcrição',
      'chave da groq',
      'groq',
      'assemblyai',
      'openrouter',
      'api key do audio',
    ],
  },
  {
    // A seção Handoff inteira ficou FORA deste índice até 2026-09-11 — sete campos que
    // existiam para quem rolava a tela e não existiam para quem procurava. Reportado de
    // tela: procurar "handoff" devolvia só o "Fallback team", que mora em Settings e é
    // outra coisa (ele é o fallback de ERRO do assign_to_team, não o roteamento do
    // pedido de humano). É o mesmo defeito do `bot-name`, na escala de uma seção.
    //
    // Desde 22/09/2026 a seção não existe: a configuração mora no diálogo do card "Pedir
    // atendimento humano", em Ferramentas. Todas as entradas abaixo levam ao CARD, e não ao
    // campo — o campo só existe com o diálogo aberto, e grifar o que não está no DOM não
    // grifa nada. O rótulo continua dizendo qual campo a pessoa procurou.
    id: 'handoff-tool',
    section: 'tools',
    anchor: 'handoff-tool',
    titulo: 'POWER_AGENTS.HANDOFF.TOOL_NAME',
    onde: 'POWER_AGENTS.HANDOFF.ONDE',
    termos: [
      'handoff',
      'humano',
      'atendente',
      'pedir humano',
      'falar com humano',
      'falar com atendente',
      'pessoa',
      'escalar',
      'transferir',
      'solicitar_humano',
    ],
  },
  {
    id: 'handoff-when',
    section: 'tools',
    anchor: 'handoff-tool',
    titulo: 'POWER_AGENTS.HANDOFF.WHEN',
    onde: 'POWER_AGENTS.HANDOFF.ONDE',
    termos: [
      'gatilho',
      'gatilhos',
      'trigger',
      'quando escalar',
      'quando pedir humano',
      'condição',
      'condicao',
      'regra de escalonamento',
      'critério',
      'criterio',
    ],
  },
  {
    id: 'handoff-silence',
    section: 'tools',
    anchor: 'handoff-tool',
    titulo: 'POWER_AGENTS.HANDOFF.SILENCE_ON_HUMAN',
    onde: 'POWER_AGENTS.HANDOFF.ONDE',
    termos: [
      'calar',
      'silenciar',
      'pausar o bot',
      'parar o bot',
      'bot responde junto',
      'bot por cima',
      'atendente assumiu',
    ],
  },
  {
    id: 'handoff-resume',
    section: 'tools',
    anchor: 'handoff-tool',
    titulo: 'POWER_AGENTS.HANDOFF.RESUME_ON_RESOLVE',
    onde: 'POWER_AGENTS.HANDOFF.ONDE',
    termos: [
      'voltar',
      'retomar',
      'religar',
      'resolver',
      'conversa resolvida',
      'bot volta',
    ],
  },
  {
    id: 'handoff-dormancy',
    section: 'tools',
    anchor: 'handoff-tool',
    titulo: 'POWER_AGENTS.HANDOFF.DORMANCY',
    onde: 'POWER_AGENTS.HANDOFF.ONDE',
    termos: [
      'dormência',
      'dormencia',
      'volta sozinho',
      'volta sozinha',
      'prazo',
      'quanto tempo calado',
      'expira',
      'horas',
      '24 horas',
    ],
  },
  {
    id: 'handoff-categories',
    section: 'tools',
    anchor: 'handoff-tool',
    titulo: 'POWER_AGENTS.HANDOFF.CATEGORIES',
    onde: 'POWER_AGENTS.HANDOFF.ONDE',
    termos: [
      'categoria',
      'categorias',
      'assunto',
      'motivo',
      'financeiro',
      'vendas',
      'suporte',
    ],
  },
  {
    id: 'handoff-routing',
    section: 'tools',
    anchor: 'handoff-tool',
    titulo: 'POWER_AGENTS.HANDOFF.ROUTING_DEFAULT',
    onde: 'POWER_AGENTS.HANDOFF.ONDE',
    termos: [
      'rotear',
      'roteamento',
      'time padrão',
      'time padrao',
      'para qual time',
      'fila',
      'encaminhar',
    ],
  },
  {
    id: 'handoff-escalation',
    section: 'tools',
    anchor: 'handoff-tool',
    titulo: 'POWER_AGENTS.HANDOFF.ESCALATION',
    onde: 'POWER_AGENTS.HANDOFF.ONDE',
    termos: [
      'escalonamento',
      'escalonar',
      'ninguém pegou',
      'ninguem pegou',
      'sem resposta',
      'outro time',
      'sla',
    ],
  },
  {
    id: 'maintenance',
    section: 'settings',
    anchor: 'maintenance',
    titulo: 'POWER_AGENTS.MAINTENANCE.TITLE',
    onde: 'POWER_AGENTS.SECTIONS.SETTINGS',
    termos: [
      'manutenção',
      'manutencao',
      'maintenance',
      'desligar',
      'pausar',
      'férias',
      'ferias',
      'fora do ar',
    ],
  },
  {
    // PERF-01 (F2-T10). Quem procura chega pelo sintoma, e não pelo nome da chave.
    id: 'contingency',
    section: 'settings',
    anchor: 'contingency',
    titulo: 'POWER_AGENTS.SETTINGS.CONTINGENCY_LABEL',
    onde: 'POWER_AGENTS.SECTIONS.SETTINGS',
    termos: [
      'contingência',
      'contingencia',
      'contingency',
      'não consegue responder',
      'falha do agente',
      'mensagem de erro',
    ],
  },
  {
    id: 'debounce',
    section: 'settings',
    anchor: 'debounce',
    titulo: 'POWER_AGENTS.MEDIA.DEBOUNCE_SECONDS',
    onde: 'POWER_AGENTS.SECTIONS.SETTINGS',
    termos: ['debounce', 'esperar', 'agrupar mensagens', 'delay', 'atraso'],
  },
  {
    // O nome que o cliente lê. Ficou de fora do índice até 2026-09-10 — o campo existia na
    // tela SEM `data-busca`, então procurar "name" não achava nada. É o caso que motivou a
    // guarda em `busca.spec.js`: índice declarado à mão diverge da tela sem avisar.
    id: 'bot-name',
    section: 'settings',
    anchor: 'bot-name',
    titulo: 'POWER_AGENTS.IDENTITY.BOT_NAME',
    onde: 'POWER_AGENTS.SECTIONS.SETTINGS',
    termos: [
      'nome',
      'name',
      'bot name',
      'nome do bot',
      'nome do agente',
      'como o agente se chama',
      'apelido',
      'display name',
    ],
  },
  {
    id: 'bot-id',
    section: 'settings',
    anchor: 'bot-id',
    titulo: 'POWER_AGENTS.SETTINGS.BOT_ID',
    onde: 'POWER_AGENTS.SECTIONS.SETTINGS',
    termos: ['id', 'bot id', 'identificador', 'curl', 'api'],
  },
  {
    id: 'photo',
    section: 'settings',
    anchor: 'photo',
    titulo: 'POWER_AGENTS.IDENTITY.PHOTO_CHANGE',
    onde: 'POWER_AGENTS.SECTIONS.SETTINGS',
    termos: ['foto', 'imagem', 'avatar', 'photo'],
  },
  {
    id: 'prompt',
    section: 'prompt',
    anchor: 'prompt',
    titulo: 'POWER_AGENTS.PROMPT.TITLE',
    onde: 'POWER_AGENTS.SECTIONS.PROMPT',
    termos: ['prompt', 'instrução', 'instrucao', 'personalidade', 'system'],
  },
  {
    id: 'model',
    section: 'identity',
    anchor: 'model',
    titulo: 'POWER_AGENTS.SECTIONS.MODELS',
    onde: 'POWER_AGENTS.SECTIONS.IDENTITY',
    termos: [
      'modelo',
      'model',
      'tier',
      'gemini',
      'gpt',
      'claude',
      'llm',
      'custo',
    ],
  },
  {
    id: 'reasoning',
    section: 'identity',
    anchor: 'reasoning',
    titulo: 'POWER_AGENTS.REASONING.TITLE',
    onde: 'POWER_AGENTS.SECTIONS.IDENTITY',
    termos: [
      'raciocínio',
      'raciocinio',
      'reasoning',
      'pensar',
      'esforço',
      'velocidade',
      'rápido',
      'demora',
    ],
  },
  {
    id: 'tools',
    section: 'tools',
    anchor: 'tools',
    titulo: 'POWER_AGENTS.SECTIONS.TOOLS',
    onde: 'POWER_AGENTS.SECTIONS.TOOLS',
    termos: [
      'tool',
      'ferramenta',
      'http',
      'webhook',
      'integração',
      'integracao',
    ],
  },
  {
    // "Add tool" e o UNICO caminho para anexar uma tool ao agente depois do redesenho de
    // 2026-09-11 — antes era um <select> no meio da pagina. Ficar fora do indice repetiria
    // exatamente o defeito que a guarda deste arquivo existe para pegar: a acao passa a
    // existir para quem rola a tela e a nao existir para quem procura.
    id: 'add-tool',
    section: 'tools',
    anchor: 'tools',
    titulo: 'POWER_AGENTS.TOOLS.ADD_TOOL',
    onde: 'POWER_AGENTS.SECTIONS.TOOLS',
    termos: [
      'adicionar ferramenta',
      'adicionar tool',
      'anexar tool',
      'nova ferramenta',
      'catalogo',
      'catálogo',
      'add tool',
    ],
  },
  // TOOLS-04: a aba Tools deixou de ser uma lista plana e ganhou busca, quatro filtros, o canal
  // de versão por tool e uma sub-aba de legado. A seção inteira tinha DUAS entradas no índice
  // ('tools' e 'variables'), e sem estas o que foi acrescentado existe para quem rola a tela e
  // não existe para quem procura — que é exatamente o defeito que as guardas deste arquivo
  // foram escritas para pegar.
  {
    id: 'tools-search',
    section: 'tools',
    anchor: 'tools-search',
    titulo: 'POWER_AGENTS.TOOLS.CATALOG_SEARCH',
    onde: 'POWER_AGENTS.SECTIONS.TOOLS',
    termos: [
      'procurar ferramenta',
      'procurar tool',
      'achar ferramenta',
      'buscar tool',
      'que tools o agente tem',
      'lista de ferramentas',
    ],
  },
  {
    // Era `tools-legacy`, apontando para a aba que saiu em TOOLS-05. Os TERMOS continuam valendo —
    // quem procura "curl" ou "criar tool http" quer exatamente este caminho —, e "legado" fica
    // porque a palavra ainda está na cabeça de quem usava a aba.
    id: 'tools-custom',
    section: 'tools',
    anchor: 'tools-add',
    titulo: 'POWER_AGENTS.TOOLS.ADD_CUSTOM',
    onde: 'POWER_AGENTS.SECTIONS.TOOLS',
    termos: [
      'legado',
      'legacy',
      'adicionar ferramenta',
      'catalogo',
      'catálogo',
      'ferramenta antiga',
      'tool antiga',
      'url da tool',
      'header da tool',
      'metodo http',
      'método http',
      'curl',
      'criar tool http',
    ],
  },
  {
    id: 'knowledge',
    section: 'knowledge',
    anchor: 'knowledge',
    titulo: 'POWER_AGENTS.SECTIONS.KNOWLEDGE',
    onde: 'POWER_AGENTS.SECTIONS.KNOWLEDGE',
    termos: ['base', 'conhecimento', 'rag', 'powerdata', 'documento', 'busca'],
  },
  {
    id: 'channel',
    section: 'channel',
    anchor: 'channel',
    titulo: 'POWER_AGENTS.SECTIONS.CHANNEL',
    onde: 'POWER_AGENTS.SECTIONS.CHANNEL',
    termos: ['caixa', 'inbox', 'whatsapp', 'canal', 'número', 'numero'],
  },
  {
    id: 'playground',
    section: 'playground',
    anchor: 'playground',
    titulo: 'POWER_AGENTS.SECTIONS.PLAYGROUND',
    onde: 'POWER_AGENTS.SECTIONS.PLAYGROUND',
    termos: ['testar', 'teste', 'playground', 'rastro', 'trace', 'depurar'],
  },
  {
    id: 'guardrails',
    section: 'guardrails',
    anchor: 'guardrails',
    titulo: 'POWER_AGENTS.GUARDRAILS.TITLE',
    onde: 'POWER_AGENTS.SECTIONS.GUARDRAILS',
    termos: [
      'guardrail',
      'guardrails',
      'proteção',
      'protecao',
      'limite',
      'bloqueio',
      'bloquear',
      'segurança',
      'seguranca',
      'link inventado',
      'url bloqueada',
      'resposta vazia',
      'cpf',
      'cnpj',
      'cartão',
      'cartao',
      'dados pessoais',
      'pii',
      'redigir',
      'chave',
      'token',
      'senha',
      'segredo',
      'termo proibido',
      'palavra proibida',
      'concorrente',
    ],
  },
  {
    id: 'variables',
    section: 'tools',
    anchor: 'variables',
    titulo: 'POWER_AGENTS.VARIABLES.DECLARED',
    onde: 'POWER_AGENTS.SECTIONS.TOOLS',
    termos: [
      'variável',
      'variavel',
      'variáveis',
      'variaveis',
      'variables',
      'placeholder',
      'substituição',
      'substituicao',
      'fuso',
      'timezone',
      'fuso horário',
    ],
  },
  {
    id: 'delete-agent',
    section: 'settings',
    anchor: 'delete-agent',
    titulo: 'POWER_AGENTS.IDENTITY.DANGER_DELETE',
    onde: 'POWER_AGENTS.SECTIONS.SETTINGS',
    termos: [
      'apagar',
      'excluir',
      'deletar',
      'delete',
      'remover agente',
      'zona de perigo',
      'danger',
    ],
  },
  // A triagem é a seção que mais gente vai procurar pelo PROBLEMA e não pelo nome: quem
  // quer resolver "o agente vende de madrugada" não sabe que a resposta se chama triagem.
  // Por isso os termos incluem o sintoma, e não só o vocabulário da feature.
  {
    id: 'triagem',
    section: 'triagem',
    anchor: 'triagem-enabled',
    titulo: 'POWER_AGENTS.TRIAGEM.ENABLED',
    onde: 'POWER_AGENTS.SECTIONS.TRIAGEM',
    termos: [
      'triagem',
      'madrugada',
      'fora do horário',
      'fora do horario',
      'horário comercial',
      'horario comercial',
      'noturno',
      'à noite',
      'a noite',
      'plantão',
      'plantao',
      'expediente',
    ],
  },
  {
    id: 'triagem-prompt',
    section: 'triagem',
    anchor: 'triagem-prompt',
    titulo: 'POWER_AGENTS.TRIAGEM.PROMPT',
    onde: 'POWER_AGENTS.SECTIONS.TRIAGEM',
    termos: [
      'prompt da triagem',
      'prompt noturno',
      'outro prompt',
      'prompt fora do horário',
      'prompt fora do horario',
    ],
  },
  {
    id: 'triagem-horario',
    section: 'triagem',
    anchor: 'triagem-horario',
    titulo: 'POWER_AGENTS.TRIAGEM.START',
    onde: 'POWER_AGENTS.SECTIONS.TRIAGEM',
    termos: [
      'janela da triagem',
      'que horas',
      'a partir de que horas',
      'início e fim',
      'inicio e fim',
      'fuso',
      'timezone',
    ],
  },
  {
    id: 'triagem-dias',
    section: 'triagem',
    anchor: 'triagem-dias',
    titulo: 'POWER_AGENTS.TRIAGEM.DAYS',
    onde: 'POWER_AGENTS.SECTIONS.TRIAGEM',
    termos: [
      'dias da semana',
      'fim de semana',
      'sábado',
      'sabado',
      'domingo',
      'segunda a sexta',
      'feriado',
    ],
  },
  {
    id: 'triagem-tools',
    section: 'triagem',
    anchor: 'triagem-tools',
    titulo: 'POWER_AGENTS.TRIAGEM.TOOLS',
    onde: 'POWER_AGENTS.SECTIONS.TRIAGEM',
    termos: [
      'tools na triagem',
      'ferramentas de madrugada',
      'limitar ferramentas',
      'bloquear tool',
      'não criar pedido',
      'nao criar pedido',
    ],
  },
  {
    id: 'triagem-trava',
    section: 'triagem',
    anchor: 'triagem-trava',
    titulo: 'POWER_AGENTS.TRIAGEM.LOCK',
    onde: 'POWER_AGENTS.SECTIONS.TRIAGEM',
    termos: [
      'trava de sessão',
      'trava de sessao',
      'trocou de personalidade',
      'mudou no meio da conversa',
      'virou outro agente',
      'no meio do atendimento',
    ],
  },
];
// Sem acento e em minúscula: quem digita "manutencao" com pressa tem de achar "manutenção".
export function normalizar(texto) {
  return String(texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

// A pontuação é o que separa "achou" de "achou o certo". Começo de palavra vale mais que
// meio de palavra, e o rótulo traduzido vale mais que um sinônimo do índice: quem digita o
// nome que está na tela espera esse campo em primeiro lugar.
function pontuar(alvo, termo) {
  if (!alvo) return 0;
  const i = alvo.indexOf(termo);
  if (i < 0) return 0;
  if (alvo === termo) return 100;
  if (i === 0) return 60;
  // Começo de palavra no meio da frase.
  return alvo[i - 1] === ' ' ? 40 : 15;
}

export function procurar(consulta, t, indice = INDICE) {
  const termo = normalizar(consulta).trim();
  if (termo.length < 2) return [];

  return indice
    .map(entrada => {
      const rotulo = t(entrada.titulo);
      // O rótulo traduzido pesa o dobro do sinônimo — ver comentário de `pontuar`.
      const doRotulo = pontuar(normalizar(rotulo), termo) * 2;
      const dosTermos = Math.max(
        0,
        ...entrada.termos.map(sinonimo => pontuar(normalizar(sinonimo), termo))
      );
      return {
        ...entrada,
        rotulo,
        secaoRotulo: t(entrada.onde),
        pontos: Math.max(doRotulo, dosTermos),
      };
    })
    .filter(r => r.pontos > 0)
    .sort((a, b) => b.pontos - a.pontos || a.rotulo.localeCompare(b.rotulo))
    .slice(0, 8);
}
